import { Controller } from "@hotwired/stimulus"

// Renders the Cloudflare Turnstile widget on public signup forms.
//
// The Cloudflare script is loaded on demand (once per page load) with
// ?render=explicit, so we control when the widget appears. That keeps it
// working across Turbo navigations and after a form re-renders with errors,
// where a plain <script> tag in the body would not run again.
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

let scriptPromise = null

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener("load", () => resolve(window.turnstile))
    script.addEventListener("error", () => {
      scriptPromise = null
      reject(new Error("Turnstile script failed to load"))
    })
    document.head.appendChild(script)
  })

  return scriptPromise
}

export default class extends Controller {
  static targets = ["widget"]
  static values = { siteKey: String }

  connect() {
    loadTurnstile()
      .then((turnstile) => {
        // Turbo may have swapped the page out while the script was loading.
        if (!this.element.isConnected) return

        this.widgetId = turnstile.render(this.widgetTarget, { sitekey: this.siteKeyValue })
      })
      .catch((error) => {
        // Server-side verification fails open, so a blocked script must not
        // leave the visitor staring at a form they cannot submit.
        console.error(error)
      })
  }

  disconnect() {
    if (this.widgetId === undefined) return

    window.turnstile?.remove(this.widgetId)
    this.widgetId = undefined
  }
}
